import { DOCUMENT, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { Faqs } from './faqs';
import metadata from './seo-metadata.json';

interface PageMeta {
  readonly title: string;
  readonly description: string;
  readonly breadcrumb: string;
  readonly image?: string;
  readonly imageAlt?: string;
  readonly imageWidth?: number;
  readonly imageHeight?: number;
}

interface SiteMeta {
  readonly name: string;
  readonly origin: string;
  readonly locale: string;
  readonly twitter: string;
  readonly defaultImage: string | null;
  readonly defaultImageAlt: string;
  readonly defaultImageWidth: number;
  readonly defaultImageHeight: number;
  readonly logo: string;
  readonly sameAs: readonly string[];
}

@Injectable({ providedIn: 'root' })
export class Seo {
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly faqs = inject(Faqs);

  private readonly site = metadata.site as SiteMeta;
  private readonly pages = metadata.pages as Record<string, PageMeta | undefined>;

  constructor() {
    // The initial navigation emits here too, so there is nothing to apply up
    // front — and `router.url` is still '/' at this point, so reading it would
    // stamp the homepage's title over a prerendered page before correcting it.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event) => this.apply(event.urlAfterRedirects));
  }

  private apply(url: string): void {
    const path = this.pathOf(url);
    const page = this.pages[path];

    // Behind a guard or mid-booking: no copy, and nothing we would want indexed.
    // Clear rather than leave the previous page's tags behind.
    if (!page) {
      this.titleService.setTitle(this.site.name);
      this.clear();
      this.setStructuredData([]);
      return;
    }

    const canonical = this.site.origin + path;
    // Dimensions have to come from the same place as the image, or a blog post
    // would advertise the default's size for its own picture.
    const image = page.image ?? this.site.defaultImage;
    const width = page.image ? page.imageWidth : this.site.defaultImageWidth;
    const height = page.image ? page.imageHeight : this.site.defaultImageHeight;

    this.titleService.setTitle(page.title);
    this.meta.updateTag({ name: 'description', content: page.description });
    this.setCanonical(canonical);

    this.meta.updateTag({ property: 'og:site_name', content: this.site.name });
    this.meta.updateTag({ property: 'og:locale', content: this.site.locale });
    this.meta.updateTag({
      property: 'og:type',
      content: path.startsWith('/blog/') ? 'article' : 'website',
    });
    this.meta.updateTag({ property: 'og:title', content: this.shareTitle(page.title) });
    this.meta.updateTag({ property: 'og:description', content: page.description });
    this.meta.updateTag({ property: 'og:url', content: canonical });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:site', content: this.site.twitter });

    if (image) {
      const src = this.site.origin + image;
      const alt = page.imageAlt ?? this.site.defaultImageAlt;

      this.meta.updateTag({ property: 'og:image', content: src });
      this.meta.updateTag({ property: 'og:image:alt', content: alt });
      this.meta.updateTag({
        property: 'og:image:type',
        content: image.endsWith('.png') ? 'image/png' : 'image/jpeg',
      });

      // Without these a scraper that will not fetch the file to measure it has
      // no idea the image is a wide banner, and falls back to the small
      // thumbnail card instead of the large one.
      if (width && height) {
        this.meta.updateTag({ property: 'og:image:width', content: String(width) });
        this.meta.updateTag({ property: 'og:image:height', content: String(height) });
      }

      // Twitter falls back to og:image, but several other clients read only
      // this one before deciding how to lay the card out.
      this.meta.updateTag({ name: 'twitter:image', content: src });
      this.meta.updateTag({ name: 'twitter:image:alt', content: alt });
    } else {
      // Only reachable if the default is ever cleared. A tag pointing at a
      // missing file is worse than none — platforms show a broken card instead
      // of falling back to the page's own images.
      this.meta.removeTag('property="og:image"');
      this.meta.removeTag('property="og:image:alt"');
      this.meta.removeTag('property="og:image:type"');
      this.meta.removeTag('property="og:image:width"');
      this.meta.removeTag('property="og:image:height"');
      this.meta.removeTag('name="twitter:image"');
      this.meta.removeTag('name="twitter:image:alt"');
    }

    // Organization belongs on one page, not all 17. Breadcrumbs are per page,
    // and the homepage is the root so it has no trail of its own.
    const blocks: object[] = [];
    if (path === '/') {
      blocks.push(this.organization(page.description));
    }
    // The homepage shows a featured handful too, but /faqs is where the full set
    // lives — two overlapping FAQPage entities would only compete.
    if (path === '/faqs') {
      blocks.push(this.faqPage());
    }
    const trail = this.breadcrumb(path);
    if (trail) {
      blocks.push(trail);
    }
    this.setStructuredData(blocks);
  }

  /**
   * Built from the same service the page renders, so a corrected answer updates
   * both at once and the two cannot drift.
   */
  private faqPage(): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.all().map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          // <strong> and <a> are both allowed here; the hrefs are made absolute
          // because a consumer reading this outside the site cannot resolve '/'.
          text: faq.a.replaceAll('href="/', `href="${this.site.origin}/`),
        },
      })),
    };
  }

  private organization(description: string): object {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: this.site.name,
      url: this.site.origin + '/',
      logo: this.site.origin + this.site.logo,
      description,
      sameAs: this.site.sameAs,
    };
  }

  /** Home > Blog > Article, built from the path. Labels are the site's own nav wording. */
  private breadcrumb(path: string): object | null {
    const root = this.pages['/'];
    if (path === '/' || !root) {
      return null;
    }

    const trail = [{ name: root.breadcrumb, url: this.site.origin + '/' }];
    let walked = '';

    for (const segment of path.split('/').filter(Boolean)) {
      walked += '/' + segment;
      const page = this.pages[walked];
      // An ancestor with no entry of its own would mean guessing at its label,
      // and a broken trail is worse than none.
      if (!page) {
        return null;
      }
      trail.push({ name: page.breadcrumb, url: this.site.origin + walked });
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: trail.map((step, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: step.name,
        item: step.url,
      })),
    };
  }

  private setStructuredData(blocks: readonly object[]): void {
    const head = this.document.head;
    // Only ever clears its own, so anything hand-written in index.html survives.
    head.querySelectorAll('script[type="application/ld+json"][data-seo]').forEach((n) => n.remove());

    for (const block of blocks) {
      const script = this.document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo', '');
      script.textContent = JSON.stringify(block);
      head.appendChild(script);
    }
  }

  /** Trailing brand is dropped for sharing — the card already shows og:site_name. */
  private shareTitle(title: string): string {
    return title.replace(/\s*\|\s*SnapSend$/, '');
  }

  private pathOf(url: string): string {
    const path = url.split(/[?#]/)[0];
    return path.length > 1 ? path.replace(/\/+$/, '') : '/';
  }

  private setCanonical(href: string): void {
    const head = this.document.head;
    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }

    link.setAttribute('href', href);
  }

  private clear(): void {
    for (const selector of [
      'name="description"',
      'property="og:title"',
      'property="og:description"',
      'property="og:url"',
      'property="og:image"',
      'property="og:image:alt"',
      'property="og:image:type"',
      'property="og:image:width"',
      'property="og:image:height"',
      'name="twitter:image"',
      'name="twitter:image:alt"',
    ]) {
      this.meta.removeTag(selector);
    }

    this.document.head.querySelector('link[rel="canonical"]')?.remove();
  }
}
